/**
 * ч.1357: створення статзвіту до Держстату — організація, рік, квартал, ознака
 * виправного звіту. Бланк заповнюється вручну на сторінці звіту.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { FORM_BY_SLUG, FORM_SHORT, FORM_TITLE, QUARTER_LABELS, previousQuarter, statReports, type StatFormSlug } from '@/api/statReports';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';

export default function StatReportCreatePage({ form }: { form: StatFormSlug }) {
  const navigate = useNavigate();
  const formCode = FORM_BY_SLUG[form];
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } = usePermissionsByOrg('reports.stat.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const prev = previousQuarter(new Date());
  const [orgId, setOrgId] = useState('');
  const [year, setYear] = useState(prev.year);
  const [quarter, setQuarter] = useState(prev.quarter);
  const [docType, setDocType] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const effectiveOrg = orgId
    || (availableOrgs.some((o) => o.id === defaultOrgId) ? defaultOrgId : '')
    || availableOrgs[0]?.id || '';

  const create = useMutation({
    mutationFn: () => statReports.create({ organization_id: effectiveOrg, form_code: formCode, period_year: year, period_quarter: quarter, doc_type: docType }),
    onSuccess: (doc) => navigate(`/stat-reports/${form}/${doc.id}`),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  if (!hasPermissionInAnyOrg) {
    return <div className="card">У вас немає прав створювати статзвіти у жодній з {totalAccessibleOrgs} організацій.</div>;
  }
  const years = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() + 1 - i).filter((y) => y >= 2025);
  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Новий звіт {FORM_SHORT[formCode]}</h2>
        <button style={{ marginLeft: 'auto' }} onClick={() => navigate(`/stat-reports/${form}`)}>до списку</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
        {FORM_TITLE[formCode]}. Звіт за календарний квартал (без наростаючого підсумку); бланк
        заповнюється вручну на сторінці звіту, перевіряється контролями Держстату і подається
        у приймальний шлюз Держстату прямо звідси або з Кабінету респондента.
      </p>
      <div className="field">
        <label>Організація</label>
        <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}>
          {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Квартал</label>
          <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
            {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{QUARTER_LABELS[q]}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Рік</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Тип</label>
          <select value={docType} onChange={(e) => setDocType(Number(e.target.value))}>
            <option value={0}>Звітний</option>
            {[1, 2, 3].map((n) => <option key={n} value={n}>Виправний №{n}</option>)}
          </select>
        </div>
      </div>
      {error && <div style={{ color: 'var(--badge-red-fg)', margin: '0.5rem 0', whiteSpace: 'pre-wrap' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <button className="primary" disabled={!effectiveOrg || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'створення…' : 'створити'}
        </button>
        <button onClick={() => navigate(`/stat-reports/${form}`)}>скасувати</button>
      </div>
    </div>
  );
}
